import { supabase } from "@/lib/supabase";

// Company/broker-facing details for the Rothbury "Make a Claim" form --
// fixed, company-wide values that don't change per claim (see migration
// 0049). Shared between the settings screen (InsuranceBrokerSettings.tsx)
// and the claim PDF generator so both read the exact same field set.
export interface InsuranceBrokerSettings {
  branch: string;
  brokerName: string;
  clientNumber: string;
  clientType: string;
  companyName: string;
  companyNumber: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  contactName: string;
  contactRelationship: string;
  contactNumber: string;
  contactEmail: string;
  hasRothburyApp: string;
  bankAccountName: string;
  bankAccountNumber: string;
  /** Comma-separated -- can hold more than one address per role. Used to
   *  pre-fill the "Email Broker" / "Email Claims Adviser" send dialogs in
   *  InsuranceClaimsTab.tsx. */
  brokerEmail: string;
  claimsAdviserEmail: string;
}

export const DEFAULT_INSURANCE_BROKER_SETTINGS: InsuranceBrokerSettings = {
  branch: "",
  brokerName: "",
  clientNumber: "",
  clientType: "Company",
  companyName: "",
  companyNumber: "",
  address1: "",
  address2: "",
  city: "",
  postalCode: "",
  contactName: "",
  contactRelationship: "",
  contactNumber: "",
  contactEmail: "",
  hasRothburyApp: "Yes",
  bankAccountName: "",
  bankAccountNumber: "",
  brokerEmail: "",
  claimsAdviserEmail: "",
};

const SETTING_NAME_BY_KEY: Record<keyof InsuranceBrokerSettings, string> = {
  branch: "insurance_broker_branch",
  brokerName: "insurance_broker_name",
  clientNumber: "insurance_client_number",
  clientType: "insurance_client_type",
  companyName: "insurance_company_name",
  companyNumber: "insurance_company_number",
  address1: "insurance_address1",
  address2: "insurance_address2",
  city: "insurance_city",
  postalCode: "insurance_postal_code",
  contactName: "insurance_contact_name",
  contactRelationship: "insurance_contact_relationship",
  contactNumber: "insurance_contact_number",
  contactEmail: "insurance_contact_email",
  hasRothburyApp: "insurance_has_rothbury_app",
  bankAccountName: "insurance_bank_account_name",
  bankAccountNumber: "insurance_bank_account_number",
  brokerEmail: "insurance_broker_email",
  claimsAdviserEmail: "insurance_claims_adviser_email",
};

/** Splits a comma-separated address field into a clean array for sending. */
export function parseEmailList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export async function loadInsuranceBrokerSettings(): Promise<InsuranceBrokerSettings> {
  const { data, error } = await supabase.from("settings").select("name, value").in("name", Object.values(SETTING_NAME_BY_KEY));
  if (error || !data) return DEFAULT_INSURANCE_BROKER_SETTINGS;

  const byName = new Map(data.map((row) => [row.name, row.value]));
  const settings = { ...DEFAULT_INSURANCE_BROKER_SETTINGS };
  (Object.keys(SETTING_NAME_BY_KEY) as (keyof InsuranceBrokerSettings)[]).forEach((key) => {
    const raw = byName.get(SETTING_NAME_BY_KEY[key]);
    if (raw != null) settings[key] = raw;
  });
  return settings;
}
